import { mixpanel } from '@affine/track';
import { captureMessage } from '@sentry/react';

import {
  CANVAS_RECONCILIATION_DIAGNOSTIC_EVENT,
  type ReconciliationDiagnosticDetail,
} from '../modules/streamlain/edge-infrastructure/diagnostics/ReconciliationInvariantMonitor';
import { type BootTraceSnapshot, SLOW_BOOT_THRESHOLD_MS } from './boot-trace';

const CANVAS_GEOMETRY_REPAIR_EVENT = 'streamlain:canvas-geometry-repair';
const MAX_CANVAS_GEOMETRY_REPAIR_REPORTS_PER_SESSION = 10;
const MAX_CANVAS_RECONCILIATION_REPORTS_PER_SESSION = 20;

type CanvasGeometryRepairDetail = {
  readonly blockId?: string;
  readonly docId?: string;
  readonly flavour?: string;
  readonly key?: string;
  readonly materialized?: boolean;
  readonly persistedValue?: unknown;
  readonly reason?: string;
  readonly repairedValue?: unknown;
  readonly yDocGuid?: string;
};

const reportedCanvasGeometryRepairs = new Set<string>();
const reportedCanvasReconciliationAnomalies = new Set<string>();

function isReconciliationDiagnosticDetail(
  detail: unknown
): detail is ReconciliationDiagnosticDetail {
  if (typeof detail !== 'object' || detail === null) return false;
  const candidate = detail as Partial<ReconciliationDiagnosticDetail>;
  return (
    typeof candidate.reconciler === 'string' &&
    typeof candidate.docId === 'string' &&
    Array.isArray(candidate.anomalyCodes)
  );
}

function installCanvasReconciliationTelemetry() {
  if (typeof window === 'undefined') return;

  window.addEventListener(CANVAS_RECONCILIATION_DIAGNOSTIC_EVENT, event => {
    const detail = (event as CustomEvent).detail;
    if (!isReconciliationDiagnosticDetail(detail)) return;

    const hasAnomaly = detail.anomalyCodes.length > 0;
    if (!hasAnomaly) return;
    const reportKey = [
      detail.docId,
      detail.reconciler,
      detail.phase,
      ...detail.anomalyCodes,
    ].join(':');
    if (
      reportedCanvasReconciliationAnomalies.has(reportKey) ||
      reportedCanvasReconciliationAnomalies.size >=
        MAX_CANVAS_RECONCILIATION_REPORTS_PER_SESSION
    ) {
      return;
    }

    reportedCanvasReconciliationAnomalies.add(reportKey);
    captureMessage('Canvas reconciliation invariant violated', {
      level: 'warning',
      tags: {
        reconciliationAnomaly: detail.anomalyCodes.join(','),
        reconciliationPhase: detail.phase,
        reconciler: detail.reconciler,
      },
    });
  });
}

function installSlowBootTelemetry() {
  if (typeof window === 'undefined') {
    return;
  }
  window.addEventListener(
    'streamlain:slow-boot',
    event => {
      const snapshot = (event as CustomEvent<BootTraceSnapshot>).detail;
      captureMessage('Application boot stalled', {
        level: 'warning',
        tags: { bootBlocker: snapshot.blocker ?? 'unknown' },
      });
    },
    { once: true }
  );
}

function firstMatchingEventMs(
  snapshot: BootTraceSnapshot,
  name: string,
  predicate: (data: Record<string, unknown>) => boolean = () => true
) {
  return snapshot.events.find(
    event => event.name === name && predicate(event.data ?? {})
  )?.atMs;
}

function installBootDurationTelemetry() {
  if (typeof window === 'undefined') {
    return;
  }
  window.addEventListener(
    'streamlain:boot-complete',
    event => {
      const snapshot = (event as CustomEvent<BootTraceSnapshot>).detail;
      mixpanel.track('AppBootCompleted', {
        durationMs: snapshot.durationMs,
        eventCount: snapshot.events.length,
        graphqlRequests: snapshot.network?.graphql,
        healthRequests: snapshot.network?.health,
        localDocStorageMs: firstMatchingEventMs(
          snapshot,
          'local-doc-storage-connected'
        ),
        navigationToReadyMs: snapshot.navigation?.appReadyMs,
        permissionCacheLocalMs: firstMatchingEventMs(
          snapshot,
          'workspace-readiness-state',
          data => data.localLoaded === true
        ),
        permissionCachePopulatedMs: firstMatchingEventMs(
          snapshot,
          'workspace-readiness-state',
          data => data.populated === true
        ),
        rootReadyMs: firstMatchingEventMs(
          snapshot,
          'workspace-readiness-state',
          data => data.rootReady === true
        ),
        slow: snapshot.durationMs >= SLOW_BOOT_THRESHOLD_MS,
        totalRequests: snapshot.network?.total,
        realtimeRequests: snapshot.network?.realtime,
        responseEndMs: snapshot.navigation?.responseEndMs,
        workspaceOpenMs: firstMatchingEventMs(snapshot, 'workspace-opened'),
      });
    },
    { once: true }
  );
}

function isCanvasGeometryRepairDetail(
  detail: unknown
): detail is CanvasGeometryRepairDetail {
  return typeof detail === 'object' && detail !== null;
}

function installCanvasGeometryRepairTelemetry() {
  if (typeof window === 'undefined') {
    return;
  }

  window.addEventListener(CANVAS_GEOMETRY_REPAIR_EVENT, event => {
    const detail = (event as CustomEvent).detail;
    if (!isCanvasGeometryRepairDetail(detail)) {
      return;
    }

    const reason = detail.reason ?? 'unknown';
    const flavour = detail.flavour ?? 'unknown';
    const key = detail.key ?? 'unknown';
    const reportKey = [
      detail.docId ?? detail.yDocGuid ?? 'unknown-doc',
      detail.blockId ?? 'unknown-block',
      key,
      reason,
    ].join(':');
    if (
      reportedCanvasGeometryRepairs.has(reportKey) ||
      reportedCanvasGeometryRepairs.size >=
        MAX_CANVAS_GEOMETRY_REPAIR_REPORTS_PER_SESSION
    ) {
      return;
    }

    reportedCanvasGeometryRepairs.add(reportKey);
    captureMessage('Canvas geometry repaired during hydration', {
      level: 'warning',
      tags: {
        blockFlavour: flavour,
        prop: key,
        repairReason: reason,
      },
    });
  });
}

// Provider initialization is owned by the consent-aware <Telemetry /> guard.
// Diagnostic listeners are installed only after that purpose is granted.
export function installDiagnosticTelemetryListeners() {
  installCanvasGeometryRepairTelemetry();
  installCanvasReconciliationTelemetry();
  installSlowBootTelemetry();
}

export function installProductAnalyticsListeners() {
  installBootDurationTelemetry();
}
